import { Injectable, Inject, OnDestroy } from '@angular/core';
import { TranslateService, LangChangeEvent } from '@ngx-translate/core';
import { StorageService } from '../storage/storage.service';
import { DOCUMENT } from '@angular/common';
import { environment } from '@env/environment';
import { Subscription } from 'rxjs';

import en from 'src/translations/en.json';
import kur from 'src/translations/kur.json';
import ar from 'src/translations/ar.json';

export interface LanguageOption {
  value: string;
  label: string;
  flag: string;
}

export function extract(s: string) {
  return s;
}

@Injectable({ providedIn: 'root' })
export class LanguageService implements OnDestroy {
  readonly supportedLanguages: string[] = environment.supportedLanguages;
  readonly defaultLanguage: string = environment.defaultLanguage;

  /** Full language options (value + label + flag) sourced from environment */
  readonly languages: LanguageOption[] = environment.languages;

  /** Currently selected language option object */
  selectedLanguage: LanguageOption;

  private langChangeSubscription!: Subscription;

  constructor(
    private translate: TranslateService,
    private storage: StorageService,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.translate.setTranslation('en', en);
    this.translate.setTranslation('kur', kur);
    this.translate.setTranslation('ar', ar);

    // Initialise selectedLanguage to the default so it's never undefined
  }

  // ─── Initialisation ───────────────────────────────────────────────────────

  init(): void {
    this.storage.getItem('lang').subscribe({
      next: res => {
        if (res == 'ar' || res == 'en' || res == 'kur') {
          this.selectedLanguage = this.findOption(res);
        } else {
          this.selectedLanguage = this.findOption(this.defaultLanguage);
        }
      }
    });

    const stored = this.storage.getLang();
    const browser = this.translate.getBrowserCultureLang() ?? '';
    const resolved = this.resolve(stored || browser);

    this.applyLanguage(resolved);

    this.langChangeSubscription = this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
      this.storage.setItem('lang', event.lang).subscribe(() => {});
      this.applyDirection(event.lang);
      this.selectedLanguage = this.findOption(event.lang);
    });
  }

  ngOnDestroy(): void {
    this.langChangeSubscription?.unsubscribe();
  }

  // ─── Public API ───────────────────────────────────────────────────────────

  get currentLang(): string {
    return this.translate.currentLang ?? this.defaultLanguage;
  }

  get getSelectedLanguage(): LanguageOption {
    return this.selectedLanguage;
  }

  get isEnglish(): boolean {
    return this.currentLang === 'en';
  }

  get isRtl(): boolean {
    return !this.isEnglish;
  }

  setLanguage(lang: string): void {
    const safe = this.resolve(lang);
    this.applyLanguage(safe);
  }

  /** Use this from the header dropdown — accepts the full option object */
  setLanguageByOption(option: LanguageOption): void {
    this.selectedLanguage = option;
    this.setLanguage(option.value);
  }

  get langChange$() {
    return this.translate.onLangChange;
  }

  // ─── Private helpers ──────────────────────────────────────────────────────

  findOption(lang: string): LanguageOption {
    return this.languages.find(l => l.value === lang) ?? this.languages.find(l => l.value === this.defaultLanguage)!;
  }

  private resolve(lang: string): string {
    if (!lang) return this.defaultLanguage;
    if (this.supportedLanguages.includes(lang)) return lang;
    const base = lang.split('-')[0];
    const prefixMatch = this.supportedLanguages.find(l => l.startsWith(base));
    if (prefixMatch) return prefixMatch;
    return this.defaultLanguage;
  }

  private applyLanguage(lang: string): void {
    this.translate.use(lang);
    this.applyDirection(lang);
  }

  private applyDirection(lang: string): void {
    const dir = lang === 'en' ? 'ltr' : 'rtl';
    this.document.documentElement.setAttribute('dir', dir);
    this.document.documentElement.setAttribute('lang', lang);
    this.document.body.classList.toggle('rtl', dir === 'rtl');
    this.document.body.classList.toggle('ltr', dir === 'ltr');
  }
}
